import React, { useState, useEffect } from "react";
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/solid";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { api_url } from "../config/config";

const ListVendors = () => {
    const [vendors, setVendors] = useState([]);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [trigger, setTrigger] = useState(false);
    const token = localStorage.getItem("admin_token");

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);
        return () => {
            clearTimeout(handler);
        };
    }, [searchTerm]);


    const totalPages = Math.ceil(vendors.length / itemsPerPage);
    const currentVendors = vendors.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handleEdit = (vendorId,vendor) => {
        navigate(`/ng-admin/edit-emplyee/${vendorId}`,{state:{
            vendorId:vendorId,
            vendor:vendor,
        }});
    };

    const handleDelete = async (user_id) => {
        try {
            await axios.delete(`${api_url}/admin/${user_id}`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
                  },
            });
            setTrigger((prev) => !prev);
            toast.success("Successfully Deleted");
        } catch (e) {
            toast.error(e.message);
        }
    };

    const handlePageChange = (page) => {
        if (page > 0 && page <= totalPages) setCurrentPage(page);
    };

    const renderPagination = () => {
        const paginationRange = [];
        const startPage = Math.max(1, currentPage - 1);
        const endPage = Math.min(totalPages, currentPage + 1);

        if (startPage > 1) paginationRange.push(1, "...");
        for (let i = startPage; i <= endPage; i++) paginationRange.push(i);
        if (endPage < totalPages) paginationRange.push("...", totalPages);

        return (
            <div className="flex items-center space-x-2">
                <ToastContainer />
                <button
                    className={`w-6 h-6 rounded-full text-sm font-medium ${
                        currentPage > 1
                            ? "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            : "bg-gray-100 text-gray-400 cursor-not-allowed"
                    }`}
                    onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                    disabled={currentPage <= 1}
                >
                    {"<"}
                </button>
                {paginationRange.map((page, index) => (
                    <button
                        key={index}
                        className={`w-6 h-6 rounded-full text-sm font-medium ${
                            currentPage === page
                                ? "bg-[#5FBDB3] text-white"
                                : "bg-gray-200 text-gray-700"
                        } hover:bg-gray-300`}
                        onClick={() => typeof page === "number" && handlePageChange(page)}
                        disabled={page === "..."}
                    >
                        {page}
                    </button>
                ))}
                <button
                    className={`w-6 h-6 rounded-full text-sm font-medium ${
                        currentPage < totalPages
                            ? "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            : "bg-gray-100 text-gray-400 cursor-not-allowed"
                    }`}
                    onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                >
                    {">"}
                </button>
            </div>
        );
    };




    const fetchVendors = async () => {
        if (!token) {
            toast.error("Unauthorized. Please log in.");
            return;
        }
        setLoading(true);
        try {
            let url = debouncedSearchTerm
                ? `${api_url}/admin?search=${debouncedSearchTerm}`
                : `${api_url}/admin`;
            const response = await axios.get(url, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
                },
            });
            const dataArr = response.data.data;
            setVendors(dataArr);


        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setCurrentPage(1)
        fetchVendors();
    }, [debouncedSearchTerm, trigger]);

    return (
        <div className="flex flex-col">
        <div className="flex-grow overflow-auto">
            <h2 className="text-2xl font-bold mb-4 mt-7">Employee List</h2>
            <div className="bg-white p-6 rounded-lg shadow-lg">
                <div className="w-full flex items-center justify-between gap-5 mb-4">
                    <input
                        type="text"
                        className="border rounded-xl border-slate-700 p-2 w-[300px] px-3"
                        placeholder="Employee Name, Phone..."
                        value={searchTerm}
                        onChange={e => {
                            setSearchTerm(e.target.value);
                        }}
                    />
                    <button
                        className="bg-[#3C1F1F] text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition"
                        onClick={() => navigate("/ng-admin/add-employee")}
                    >
                        Add Members
                    </button>
                </div>
                <div className="overflow-x-auto sidebar-scroll">
                <table className="table-auto w-full text-sm text-left">
                    <thead className="bg-gray-100">
                        <tr>
                            <th className="px-6 py-3">Employee ID</th>
                            <th className="px-6 py-3">Employee Name</th>
                            <th className="px-6 py-3">Email</th>
                            <th className="px-6 py-3">Phone</th>
                            <th className="px-6 py-3">Branch</th>
                            <th className="px-6 py-3">Designation</th>
                            <th className="px-6 py-3">Role</th>
                            <th className="px-6 py-3">Status</th>
                            <th className="px-6 py-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="overflow-y-auto max-h-[400px]">
                        {currentVendors.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="text-center py-8 text-gray-500">
                                    No employees found matching your search.
                                </td>
                            </tr>
                        ) : (
                            currentVendors.map((vendor) => (
                                <tr key={vendor.id} className="hover:bg-gray-100">
                                    <td className="px-6 py-3">{vendor.employee_id || "N/A"}</td>
                                    <td className="px-6 py-3">{vendor.name}</td>
                                    <td className="px-6 py-3">{vendor.email}</td>
                                    <td className="px-6 py-3">{vendor.mobile_number}</td>
                                    <td className="px-6 py-3">{vendor.branch?.area || "N/A"}</td>
                                    <td className="px-6 py-3">
                                        {vendor.designation ? vendor.designation : "N/A"}
                                    </td>
                                    <td className="px-6 py-3">{vendor.role}</td>
                                    <td className="px-6 py-3">
                                <span className={vendor.isActive ? "text-green-600 font-semibold" : "text-red-600 font-semibold"}>
                                    {vendor.isActive ? "Active" : "Inactive"}
                                </span>
                                </td>

                                    <td className="px-6 py-3 flex space-x-2">
                                        <PencilSquareIcon
                                            className="h-5 w-5 text-[#3C1F1F] cursor-pointer hover:text-blue-700"
                                            onClick={() => handleEdit(vendor.user_id, vendor)}
                                        />
                                        <TrashIcon
                                            className="h-5 w-5 text-red-500 cursor-pointer hover:text-red-700"
                                            onClick={() => handleDelete(vendor.user_id)}
                                        />
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
                </div>
                {renderPagination()}
            </div>
        </div>
    </div>
    
    );
};

export default ListVendors;
